import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useApiClient } from '../../../utils/apiClient';
import { FiChevronLeft, FiChevronRight, FiSave, FiX, FiAlertCircle } from 'react-icons/fi';
import { useAuth } from '../../../contexts/AuthContext';
import { useBusinessSettings } from '../../../hooks/useBusinessSettings';
import { useInvoiceCalculator } from '../../../hooks/useInvoiceCalculator';
import { useCustomers } from '../../../hooks/useCustomers';
import { useItems } from '../../../hooks/useItems';
import InvoiceItemsEditor from './InvoiceItemsEditor';
import InvoiceSummaryCard from './InvoiceSummaryCard';
import { formatYYYYMMDDLocal, addDaysYYYYMMDD } from '../../../utils/dateOnly';

interface Customer {
  id: string;
  first_name: string;
  last_name: string;
  company_name?: string;
  display_name?: string;
  email: string;
  tax_exempt?: boolean;
}

interface InvoiceItem {
  id?: string;
  item_id?: string;
  client_line_key?: string;
  line_order?: number;
  name?: string;
  description: string;
  qty: number;
  rate: number;
  amount: number;
  is_taxable?: boolean;
}

interface DocumentFormData {
  id?: string;
  customer_id: string;
  number?: string;
  issue_date: string;
  due_date?: string;
  expiry_date?: string;
  status: string;
  terms?: string;
  notes?: string;
  converted_invoice_id?: string | null;
  items: InvoiceItem[];
}

type WizardStep = 'edit' | 'review';
type NumericItemField = 'qty' | 'rate' | 'amount';
const NUMERIC_ITEM_FIELDS: NumericItemField[] = ['qty', 'rate', 'amount'];

interface InvoiceFormWizardProps {
  isOpen?: boolean;
  onClose?: () => void;
  onSuccess?: () => void;
  customerId?: string;
  invoiceId?: string; // For editing existing invoices
  estimateId?: string;
  mode?: 'invoice' | 'estimate';
  presentation?: 'modal' | 'page';
}

const InvoiceFormWizard: React.FC<InvoiceFormWizardProps> = ({ 
  isOpen = true,
  onClose, 
  onSuccess,
  customerId,
  invoiceId,
  estimateId,
  mode = 'invoice',
  presentation = 'modal',
}) => {
  const navigate = useNavigate();
  const { token, currentBusiness, isCookieMode, API_BASE_URL } = useAuth();
  const api = useApiClient();
  const { settings: businessSettings } = useBusinessSettings();
  const { customers, customersById } = useCustomers();
  const isEstimateMode = mode === 'estimate';
  const documentId = isEstimateMode ? estimateId : invoiceId;
  const isEditingDocument = Boolean(documentId);
  const isPagePresentation = presentation === 'page';
  const documentLabel = isEstimateMode ? 'Estimate' : 'Invoice';
  const documentLabelLower = isEstimateMode ? 'estimate' : 'invoice';
  const documentListLabel = isEstimateMode ? 'estimates' : 'invoices';
  const numberPrefix = isEstimateMode ? 'EST-' : 'INV-';
  const createHeading = `${isEditingDocument ? 'Edit' : 'Create'} ${documentLabel}`;
  const headingDescription = isEditingDocument
    ? `Update ${documentLabelLower} details, dates, line items, and totals before saving.`
    : `Create a new ${documentLabelLower} with customer details, line items, and a live summary.`;
  const listPath = isEstimateMode ? '/estimates' : '/invoices';
  const detailsHeading = `${documentLabel} Details`;
  const secondaryDateField = isEstimateMode ? 'expiry_date' : 'due_date';
  const secondaryDateLabel = isEstimateMode ? 'Expiry Date' : 'Due Date';
  const summaryTotalLabel = isEstimateMode ? 'Total Estimate' : 'Total';
  const shouldDisableLateFees = isEstimateMode;
  const clientLineKeyCounterRef = useRef(0);
  const createClientLineKey = () => `client-line-${clientLineKeyCounterRef.current++}`;
  const withClientLineKeys = (items: InvoiceItem[]) =>
    items.map((item) => ({
      ...item,
      client_line_key: item.client_line_key || item.id || createClientLineKey(),
    }));
  const stripClientLineFields = (items: InvoiceItem[]) =>
    items.map(({ client_line_key, line_order, ...item }) => item);
  const getSaveErrorMessage = (err: any) => {
    if (typeof err?.message === 'string' && err.message.trim()) {
      return err.message;
    }
    return `Failed to save ${documentLabelLower}`;
  };
  
  const [currentStep, setCurrentStep] = useState<WizardStep>('edit');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [itemNumericDrafts, setItemNumericDrafts] = useState<Record<string, string>>({});
  const [itemNumericErrors, setItemNumericErrors] = useState<Record<string, string>>({});
  
  const [pendingSaveConfirmation, setPendingSaveConfirmation] = useState<DocumentFormData | null>(null);
  const [showMismatchWarning, setShowMismatchWarning] = useState(false);
  const [formData, setFormData] = useState<DocumentFormData>({
    customer_id: '',
    issue_date: formatYYYYMMDDLocal(businessSettings?.timezone),
    due_date: addDaysYYYYMMDD(formatYYYYMMDDLocal(businessSettings?.timezone), 30, businessSettings?.timezone),
    expiry_date: addDaysYYYYMMDD(formatYYYYMMDDLocal(businessSettings?.timezone), 30, businessSettings?.timezone),
    status: isEstimateMode ? 'pending' : 'open',
    terms: '',
    notes: '',
    converted_invoice_id: null,
    items: [
      {
        description: '',
        qty: 1,
        rate: 0,
        amount: 0,
        is_taxable: false,
        client_line_key: createClientLineKey()
      }
    ]
  });

  // Price-aware items list based on selected customer
  const { items: availableItems } = useItems(formData.customer_id || undefined);

  const [nextDocumentNumber, setNextDocumentNumber] = useState<string>('');
  const selectedCustomer = formData.customer_id ? customersById[formData.customer_id] : undefined;
  const customerTaxExempt = selectedCustomer?.tax_exempt === true;

  // Accessibility refs
  const stepRefs = useRef<{ [key in WizardStep]: HTMLDivElement | null }>({
    edit: null,
    review: null
  });
  const errorRef = useRef<HTMLDivElement>(null);
  const isDirtyRef = useRef<boolean>(false);

  // Set customer_id from prop if provided
  useEffect(() => {
    if (customerId && formData.customer_id !== customerId) {
      setFormData(prev => ({ ...prev, customer_id: customerId }));
      
    }
  }, [customerId, formData.customer_id]);

  useEffect(() => {
    if (documentId) return;
    const today = formatYYYYMMDDLocal(businessSettings?.timezone);
    const plusThirty = addDaysYYYYMMDD(today, 30, businessSettings?.timezone);
    setFormData((prev) => ({
      ...prev,
      issue_date: prev.issue_date || today,
      due_date: isEstimateMode ? '' : (prev.due_date || plusThirty),
      expiry_date: isEstimateMode ? (prev.expiry_date || plusThirty) : (prev.expiry_date || ''),
      status: isEstimateMode ? 'pending' : 'open'
    }));
  }, [businessSettings?.timezone, documentId, isEstimateMode]);

  // Focus management for accessibility
  useEffect(() => {
    if (stepRefs.current[currentStep]) {
      stepRefs.current[currentStep]?.focus();
    }
  }, [currentStep]);

  // Announce errors to screen readers
  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.focus();
    }
  }, [error]);

  // Fetch next document number on mount (for preview)
  useEffect(() => {
    if (!documentId && isOpen) {
      const headers: any = {
      };
      
      // Only add Authorization header if not in cookie mode
      if (!isCookieMode && token) {
        headers.Authorization = `Bearer ${token}`;
      }
      
      axios.get(`${API_BASE_URL}${isEstimateMode ? '/estimates' : '/invoices'}/next-number`, {
        headers,
        withCredentials: isCookieMode,
      })
        .then(res => {
          setNextDocumentNumber(res.data.nextNumber);
        })
        .catch(() => setNextDocumentNumber(''));
    }
  }, [documentId, isEstimateMode, isOpen, token, currentBusiness, API_BASE_URL, isCookieMode]);

  // Keyboard shortcut: Ctrl/Cmd + S to save on review; otherwise go to review if allowed
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        if (currentStep === 'review') {
          handleSubmit();
        } else if (currentStep === 'edit' && (formData.customer_id && formData.issue_date && (isEstimateMode ? formData.expiry_date : formData.due_date))) {
          setCurrentStep('review');
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [currentStep, formData.customer_id, formData.issue_date, formData.due_date, formData.expiry_date, isEstimateMode]);

  const fetchDocument = async (id: string) => {
    setLoading(true);
    try {
      const headers: any = {
      };
      
      // Only add Authorization header if not in cookie mode
      if (!isCookieMode && token) {
        headers.Authorization = `Bearer ${token}`;
      }
      
      const response = await axios.get(`${API_BASE_URL}${isEstimateMode ? '/estimates' : '/invoices'}/${id}`, {
        headers,
        withCredentials: isCookieMode,
      });
      const document = response.data;
      const normalizeDate = (d: any) => {
        if (!d) return '';
        if (typeof d === 'string') return d.includes('T') ? d.split('T')[0] : d;
        if (d instanceof Date) return d.toISOString().split('T')[0];
        return '';
      };
      setFormData({
        customer_id: document.customer_id,
        number: document.number,
        issue_date: normalizeDate(document.issue_date),
        due_date: normalizeDate(document.due_date),
        expiry_date: normalizeDate(document.expiry_date),
        status: document.status,
        terms: document.terms || '',
        notes: document.notes || '',
        converted_invoice_id: document.converted_invoice_id || null,
        items: withClientLineKeys(document.items || [])
      });
    } catch (err: any) {
      setError(`Failed to fetch ${documentLabelLower}`);
    } finally {
      setLoading(false);
    }
  };

  // Only fetch document if editing (documentId is present)
  useEffect(() => {
    if (currentBusiness && documentId) {
      fetchDocument(documentId);
    }
  }, [currentBusiness, documentId, token, API_BASE_URL, isEstimateMode]);

  useEffect(() => {
    if (!customerTaxExempt) return;
    if (!formData.items.some((item) => item.is_taxable)) return;
    setFormData((prev) => ({
      ...prev,
      items: prev.items.map((item) => ({ ...item, is_taxable: false })),
    }));
  }, [customerTaxExempt, formData.items]);

  // Auto-calculate line item amount when qty or rate changes
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map(item => ({
        ...item,
        amount: (Number(item.qty) || 0) * (Number(item.rate) || 0)
      }))
    }));
    // Only run when qty or rate changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.items.map(item => `${item.qty}:${item.rate}`).join(',')]);

  const handleFormChange = (field: keyof DocumentFormData, value: any) => {
    isDirtyRef.current = true;
    if (field === 'number') {
      const raw: string = typeof value === 'string' ? value : '';
      const trimmed = raw.replace(/^\s+/, '');
      const sanitized = trimmed === ''
        ? ''
        : (trimmed.startsWith(numberPrefix) ? trimmed : `${numberPrefix}${trimmed.replace(/^(INV|EST)[-\s]?/i, '')}`);
      setFormData(prev => ({ ...prev, number: sanitized }));
      return;
    }
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleItemChange = (index: number, field: keyof InvoiceItem, value: any) => {
    isDirtyRef.current = true;
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? { ...item, [field]: field === 'is_taxable' && customerTaxExempt ? false : value } : item
      )
    }));
  };

  const getNumericItemFieldKey = (index: number, field: NumericItemField) => `${index}:${field}`;

  const getNumericItemFieldDisplayValue = (index: number, field: NumericItemField) => {
    const key = getNumericItemFieldKey(index, field);
    if (Object.prototype.hasOwnProperty.call(itemNumericDrafts, key)) {
      return itemNumericDrafts[key];
    }
    const value = formData.items[index]?.[field];
    if (value === null || value === undefined) return '';
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? String(value) : '';
  };

  const parseNumericItemDraft = (rawValue: string) => {
    const trimmed = rawValue.trim();
    if (trimmed === '') {
      return { isValid: false, message: 'Required' };
    }
    const parsedValue = Number(trimmed);
    if (!Number.isFinite(parsedValue) || parsedValue < 0) {
      return { isValid: false, message: 'Enter a valid non-negative number' };
    }
    return { isValid: true, parsedValue };
  };

  const onNumericItemDraftChange = (index: number, field: NumericItemField, rawValue: string) => {
    const key = getNumericItemFieldKey(index, field);
    setItemNumericDrafts(prev => ({ ...prev, [key]: rawValue }));
    setItemNumericErrors(prev => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const onNumericItemFieldBlur = (index: number, field: NumericItemField) => {
    const key = getNumericItemFieldKey(index, field);
    if (!Object.prototype.hasOwnProperty.call(itemNumericDrafts, key)) return;

    const result = parseNumericItemDraft(itemNumericDrafts[key] || '');
    if (!result.isValid) {
      setItemNumericErrors(prev => ({ ...prev, [key]: result.message || 'Invalid value' }));
      return;
    }

    handleItemChange(index, field, result.parsedValue);
    setItemNumericDrafts(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setItemNumericErrors(prev => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const validateAndNormalizeNumericItems = () => {
    const nextErrors: Record<string, string> = {};
    const normalizedItems = formData.items.map((item, index) => {
      const nextItem = { ...item };
      for (const field of NUMERIC_ITEM_FIELDS) {
        const key = getNumericItemFieldKey(index, field);
        if (Object.prototype.hasOwnProperty.call(itemNumericDrafts, key)) {
          const draftResult = parseNumericItemDraft(itemNumericDrafts[key] || '');
          if (!draftResult.isValid) {
            nextErrors[key] = draftResult.message || 'Invalid value';
            continue;
          }
          nextItem[field] = draftResult.parsedValue as number;
        }
        const finalValue = Number(nextItem[field]);
        if (!Number.isFinite(finalValue) || finalValue < 0) {
          nextErrors[key] = 'Required';
        }
      }
      return nextItem;
    });

    setItemNumericErrors(nextErrors);

    const firstInvalidKey = Object.keys(nextErrors)[0];
    if (firstInvalidKey) {
      setTimeout(() => {
        const input = document.querySelector<HTMLInputElement>(`[data-item-field-key="${firstInvalidKey}"]`);
        input?.focus();
      }, 0);
      return { isValid: false, normalizedItems };
    }

    setItemNumericDrafts({});
    setItemNumericErrors({});
    return { isValid: true, normalizedItems };
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, {
        description: '',
        qty: 1,
        rate: 0,
        amount: 0,
        is_taxable: false,
        client_line_key: createClientLineKey()
      }]
    }));
    setItemNumericErrors({});
  };

  const nextStep = () => {
    const currentIndex = steps.indexOf(currentStep);
    if (currentStep === 'edit') {
      const normalizedResult = validateAndNormalizeNumericItems();
      if (!normalizedResult.isValid) {
        setError('Please complete required numeric fields for all line items');
        return;
      }
      setFormData(prev => ({ ...prev, items: normalizedResult.normalizedItems }));
      setError('');
    }
    if (currentIndex < steps.length - 1) {
      setCurrentStep(steps[currentIndex + 1]);
    }
  };

  const prevStep = () => {
    const currentIndex = steps.indexOf(currentStep);
    if (currentIndex > 0) {
      setCurrentStep(steps[currentIndex - 1]);
    }
  };

  const saveDocument = async (normalizedFormData: DocumentFormData) => {
    // Calculate sub_total (sum of all line item amounts, excluding tax)
    const sub_total = normalizedFormData.items.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
    // Client-side guard: block net-negative documents before POST
    const taxRate = businessSettings?.tax_rate ?? 8.0;
    const taxableAmount = customerTaxExempt ? 0 : normalizedFormData.items.filter(i => i.is_taxable).reduce((s, i) => s + (Number(i.amount) || 0), 0);
    const taxAmount = taxableAmount * (Number(taxRate) / 100);
    if (sub_total + taxAmount < 0) {
      setSaving(false);
      setError(`${documentLabel} total < 0. Use credit memo flow.`);
      return false;
    }

    const payload: any = {
      customer_id: normalizedFormData.customer_id,
      number: normalizedFormData.number,
      issue_date: normalizedFormData.issue_date,
      terms: normalizedFormData.terms,
      notes: normalizedFormData.notes,
      status: normalizedFormData.status,
      items: stripClientLineFields(normalizedFormData.items),
      sub_total,
    };

    if (isEstimateMode) {
      payload.expiry_date = normalizedFormData.expiry_date || undefined;
    } else {
      payload.due_date = normalizedFormData.due_date || undefined;
    }

    if (documentId) {
      await api.put(`${isEstimateMode ? '/estimates' : '/invoices'}/${documentId}`, payload);
    } else {
      await api.post(`${isEstimateMode ? '/estimates' : '/invoices'}`, payload);
    }

    return true;
  };

  const handleSubmit = async (confirmedEstimateMismatch: boolean = false) => {
    const normalizedResult = validateAndNormalizeNumericItems();
    if (!normalizedResult.isValid) {
      setError('Please complete required numeric fields for all line items');
      return;
    }
    const normalizedFormData = {
      ...formData,
      items: normalizedResult.normalizedItems
    };
    setFormData(normalizedFormData);

    setSaving(true);
    setError('');
    
    try {
      if (
        isEstimateMode &&
        documentId &&
        normalizedFormData.status === 'approved' &&
        normalizedFormData.converted_invoice_id &&
        !confirmedEstimateMismatch
      ) {
        setPendingSaveConfirmation(normalizedFormData);
        setShowMismatchWarning(true);
        setSaving(false);
        return;
      }

      const saved = await saveDocument(normalizedFormData);
      if (!saved) return;

      if (onSuccess) {
        onSuccess();
      } else if (documentId) {
        const savedCustomerId = normalizedFormData.customer_id || formData.customer_id;
        if (savedCustomerId) {
          navigate(`/customers/${savedCustomerId}`);
        } else {
          navigate(listPath);
        }
      } else {
        navigate(listPath);
      }
    } catch (err: any) {
      setError(getSaveErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmedEstimateMismatchSave = async () => {
    if (!pendingSaveConfirmation) return;
    setShowMismatchWarning(false);
    setSaving(true);
    setError('');
    try {
      const saved = await saveDocument(pendingSaveConfirmation);
      if (!saved) return;
      setPendingSaveConfirmation(null);
      if (onSuccess) {
        onSuccess();
      } else if (documentId) {
        const savedCustomerId = pendingSaveConfirmation.customer_id || formData.customer_id;
        if (savedCustomerId) {
          navigate(`/customers/${savedCustomerId}`);
        } else {
          navigate(listPath);
        }
      } else {
        navigate(listPath);
      }
    } catch (err: any) {
      setError(getSaveErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const getCustomerDisplayName = (customer: Customer) => {
    return customer.display_name || customer.company_name || `${customer.first_name} ${customer.last_name}`;
  };

  const { totals, formatCurrency } = useInvoiceCalculator(
    formData.items, 
    isEstimateMode ? formData.expiry_date : formData.due_date, 
    businessSettings ? {
      tax_rate: businessSettings.tax_rate || 8.00,
      late_fee_mode: businessSettings.late_fee_mode || 'flat',
      late_fee_amount: businessSettings.late_fee_amount || 25.00,
      currency: businessSettings.currency || 'USD'
    } : undefined,
    { customerTaxExempt, disableLateFees: shouldDisableLateFees }
  );

  const steps: WizardStep[] = ['edit', 'review'];
  const currentStepIndex = steps.indexOf(currentStep);
  const canProceedToReview = Boolean(formData.customer_id && formData.issue_date && (isEstimateMode ? formData.expiry_date : formData.due_date));

  const handleSaveAndCreateAnother = async () => {
    const normalizedResult = validateAndNormalizeNumericItems();
    if (!normalizedResult.isValid) {
      setError('Please complete required numeric fields for all line items');
      return;
    }
    const normalizedFormData = {
      ...formData,
      items: normalizedResult.normalizedItems
    };
    setFormData(normalizedFormData);

    setSaving(true);
    setError('');
    try {
      const saved = await saveDocument(normalizedFormData);
      if (!saved) return;

      // Reset to a new document, preserve customer and dates
      setFormData(prev => ({
        customer_id: prev.customer_id,
        issue_date: prev.issue_date,
        due_date: isEstimateMode ? '' : prev.due_date,
        expiry_date: isEstimateMode ? prev.expiry_date : '',
        status: isEstimateMode ? 'pending' : 'open',
        terms: '',
        notes: '',
        number: '',
        converted_invoice_id: null,
        items: []
      }));
      setCurrentStep('edit');

      try {
        const headers: any = {};
        if (!isCookieMode && token) {
          headers.Authorization = `Bearer ${token}`;
        }
        const res = await axios.get(`${API_BASE_URL}${isEstimateMode ? '/estimates' : '/invoices'}/next-number`, {
          headers,
          withCredentials: isCookieMode,
        });
        setNextDocumentNumber(res.data.nextNumber);
      } catch (_) {
        setNextDocumentNumber('');
      }
    } catch (err: any) {
      setError(getSaveErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isOpen) {
    return null;
  }

  if (customers.length === 0) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600 dark:text-gray-400">Loading customers...</p>
        </div>
      </div>
    );
  }

  const dismissWizard = () => {
    if (isDirtyRef.current && !window.confirm('You have unsaved changes. Discard them?')) return;
    onClose?.();
  };

  const wizardContent = (
    <>
      <div className={`flex items-start justify-between gap-4 border-b border-gray-200 dark:border-neutral-800 ${isPagePresentation ? 'px-5 py-5 sm:px-6' : 'p-6'} bg-white/80 dark:bg-neutral-900/80 backdrop-blur`}>
        <div className="min-w-0">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            {isEditingDocument ? `Editing ${documentLabelLower}` : `New ${documentLabelLower}`}
          </div>
          <h1 id="invoices-heading" className="mt-1 text-xl font-extrabold tracking-tight text-gray-900 dark:text-white sm:text-2xl">
            {createHeading}
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{headingDescription}</p>
        </div>
        <button
          onClick={dismissWizard}
          className="inline-flex items-center gap-2 rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra dark:bg-neutral-900 dark:border-neutral-700 dark:text-neutral-200"
          aria-label={isPagePresentation ? `Back to ${documentLabelLower}` : 'Close dialog'}
        >
          <FiX className="h-5 w-5" />
          <span className="hidden sm:inline">
            {isPagePresentation
              ? (isEditingDocument ? `Back to ${documentLabelLower}` : `Back to ${documentListLabel}`)
              : 'Close'}
          </span>
        </button>
      </div>

      <div className={`mx-auto ${isPagePresentation ? 'max-w-6xl px-5 py-5 sm:px-6' : 'max-w-7xl px-6 py-6 lg:px-8'}`}>
        <div className="mx-auto max-w-6xl">
          <nav
            className={`${isPagePresentation ? 'mb-6' : 'mb-8'}`}
            role="navigation"
            aria-label={`${documentLabel} creation progress`}
          >
            <div role="tablist" aria-label={`${documentLabel} creation steps`} className="flex flex-wrap gap-2">
              {steps.map((step, index) => (
                <button
                  key={step}
                  onClick={() => setCurrentStep(step as WizardStep)}
                  className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold ${
                    currentStep === step
                      ? 'border-sedra/30 bg-sedra/10 text-sedra'
                      : 'border-gray-300 bg-white text-gray-600 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-300'
                  }`}
                  aria-label={`Go to ${step} step`}
                  role="tab"
                  aria-selected={currentStep === step}
                  id={`step-${index + 1}`}
                >
                  <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-black/5 text-[11px] dark:bg-white/10">
                    {index + 1}
                  </span>
                  <span>{step === 'edit' ? 'Edit' : 'Review'}</span>
                </button>
              ))}
            </div>
          </nav>

          {error && (
            <div
              ref={errorRef}
              className="mb-6 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-200"
              role="alert"
              aria-live="assertive"
            >
              <div className="flex items-start gap-2">
                <FiAlertCircle className="h-5 w-5" aria-hidden="true" />
                <p>{error}</p>
              </div>
            </div>
          )}

          {loading && (
            <div
              className="mb-6 rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-700 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-200"
              role="status"
              aria-live="polite"
            >
              <p>Loading {documentLabelLower} data...</p>
            </div>
          )}

          {!loading && (
            <div className="sr-only" role="status" aria-live="polite">
              {documentLabel} form ready
            </div>
          )}

          <main role="main">
            {currentStep === 'edit' && (
              <div
                ref={(el) => { stepRefs.current.edit = el; }}
                className={`${isPagePresentation ? 'space-y-5' : 'space-y-6'}`}
                role="tabpanel"
                aria-labelledby={`step-${currentStepIndex + 1}`}
                tabIndex={-1}
              >
                <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
                  <div className="space-y-5 lg:col-span-8">
                    <div className="flex items-center justify-between gap-3">
                      <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500">{detailsHeading}</h2>
                      {isEditingDocument && formData.number ? (
                        <span className="rounded-full bg-sedra/10 px-3 py-1 text-xs font-semibold text-sedra">
                          {formData.number}
                        </span>
                      ) : null}
                    </div>

                    <div className="rounded-2xl border border-gray-200 bg-white p-4 elev-1 dark:bg-neutral-900 dark:border-neutral-800">
                      <label htmlFor="customer-select" className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Customer *
                      </label>
                      <select
                        id="customer-select"
                        value={formData.customer_id}
                        onChange={(e) => handleFormChange('customer_id', e.target.value)}
                        className="mt-1 block w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-gray-900 placeholder:text-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-sedra focus:ring-offset-2 dark:bg-neutral-900 dark:border-neutral-700 dark:text-white"
                        required
                        aria-describedby="customer-help"
                        aria-invalid={!formData.customer_id}
                        tabIndex={0}
                      >
                        <option value="">Select a customer</option>
                        {customers.map(customer => (
                          <option key={customer.id} value={customer.id}>
                            {getCustomerDisplayName(customer)}
                          </option>
                        ))}
                      </select>
                      <p id="customer-help" className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        Choose the customer for this {documentLabelLower}
                      </p>
                    </div>

                    <div className="rounded-2xl border border-gray-200 bg-white p-4 elev-1 dark:bg-neutral-900 dark:border-neutral-800">
                      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                        <div>
                          <label htmlFor="issue-date" className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Issue Date *
                          </label>
                          <input
                            id="issue-date"
                            type="date"
                            value={formData.issue_date}
                            onChange={(e) => handleFormChange('issue_date', e.target.value)}
                            className="mt-1 block w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-gray-900 placeholder:text-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-sedra focus:ring-offset-2 dark:bg-neutral-900 dark:border-neutral-700 dark:text-white"
                            required
                            aria-describedby="issue-date-help"
                          />
                          <p id="issue-date-help" className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                            The date this {documentLabelLower} was issued
                          </p>
                        </div>
                        <div>
                          <label htmlFor="due-date" className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                            {secondaryDateLabel} *
                          </label>
                          <input
                            id="due-date"
                            type="date"
                            value={isEstimateMode ? (formData.expiry_date || '') : (formData.due_date || '')}
                            onChange={(e) => handleFormChange(secondaryDateField as keyof DocumentFormData, e.target.value)}
                            className="mt-1 block w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-gray-900 placeholder:text-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-sedra focus:ring-offset-2 dark:bg-neutral-900 dark:border-neutral-700 dark:text-white"
                            required
                            aria-describedby="due-date-help"
                          />
                          <p id="due-date-help" className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                            {isEstimateMode ? 'The date this estimate expires' : 'The date payment is due'}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="rounded-2xl border border-gray-200 bg-white p-4 elev-1 dark:bg-neutral-900 dark:border-neutral-800">
                      <label htmlFor="invoice-number" className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                        {documentLabel} Number
                      </label>
                      <input
                        id="invoice-number"
                        type="text"
                        value={formData.number || ''}
                        onChange={(e) => handleFormChange('number', e.target.value)}
                        placeholder="Auto-generated if left empty"
                        className="mt-1 block w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-gray-900 placeholder:text-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-sedra focus:ring-offset-2 dark:bg-neutral-900 dark:border-neutral-700 dark:text-white"
                        aria-describedby="invoice-number-help"
                      />
                      <p id="invoice-number-help" className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        Leave empty for auto-generated number
                      </p>
                    </div>

                    <div className="rounded-2xl border border-gray-200 bg-white p-4 elev-1 dark:bg-neutral-900 dark:border-neutral-800">
                      <label htmlFor="terms" className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Terms & Notes
                      </label>
                      <textarea
                        id="terms"
                        value={formData.terms || ''}
                        onChange={(e) => handleFormChange('terms', e.target.value)}
                        rows={3}
                        className="mt-1 block w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-gray-900 placeholder:text-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-sedra focus:ring-offset-2 dark:bg-neutral-900 dark:border-neutral-700 dark:text-white"
                        aria-describedby="terms-help"
                      />
                      <p id="terms-help" className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        Additional terms or notes for the customer
                      </p>
                    </div>

                    <div className="space-y-4">
                      <InvoiceItemsEditor
                        items={formData.items}
                        availableItems={availableItems}
                        onItemsChange={(items) => {
                          setFormData(prev => ({ ...prev, items: withClientLineKeys(items) }));
                          setItemNumericDrafts({});
                          setItemNumericErrors({});
                        }}
                        onItemChange={handleItemChange}
                        customerId={formData.customer_id}
                        customerTaxExempt={customerTaxExempt}
                        getNumericItemFieldDisplayValue={getNumericItemFieldDisplayValue}
                        getNumericItemFieldKey={getNumericItemFieldKey}
                        onNumericItemDraftChange={onNumericItemDraftChange}
                        onNumericItemFieldBlur={onNumericItemFieldBlur}
                        itemNumericErrors={itemNumericErrors}
                        compactNumericHelpers={true}
                      />
                    </div>
                  </div>

                  <div className="lg:col-span-4">
                    <div className="lg:sticky lg:top-6">
                      <InvoiceSummaryCard
                        totals={totals}
                        taxRate={businessSettings?.tax_rate || 8}
                        nextInvoiceNumber={nextDocumentNumber}
                        explicitInvoiceNumber={formData.number}
                        formatCurrency={formatCurrency}
                        documentLabel={documentLabelLower}
                        totalLabel={summaryTotalLabel}
                        hideLateFee={isEstimateMode}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {currentStep === 'review' && (
              <div
                ref={(el) => { stepRefs.current.review = el; }}
                className="space-y-5"
                role="tabpanel"
                aria-labelledby={`step-${currentStepIndex + 1}`}
                tabIndex={-1}
              >
                <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  {isEditingDocument ? 'Review Changes' : 'Review & Save'}
                </h2>

                <div className="grid grid-cols-1 gap-5 lg:grid-cols-12" role="region" aria-label={`${documentLabel} review`}>
                  <div className="space-y-5 lg:col-span-7">
                    <div className="rounded-2xl border border-gray-200 bg-white p-4 elev-1 dark:bg-neutral-900 dark:border-neutral-800">
                      <div className="mb-4 flex items-start justify-between gap-3">
                        <div>
                          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{detailsHeading}</h3>
                          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                            Confirm the recipient, numbering, and dates before saving this {documentLabelLower}.
                          </p>
                        </div>
                        <div className="rounded-full bg-sedra/10 px-3 py-1 text-xs font-semibold text-sedra">
                          {formData.items.length} item{formData.items.length === 1 ? '' : 's'}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <div className="rounded-xl border border-gray-200 bg-gray-50/70 p-4 dark:border-neutral-800 dark:bg-neutral-950/40">
                          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Customer</div>
                          <p className="mt-2 text-sm font-semibold text-gray-900 dark:text-white">
                            {formData.customer_id && customersById[formData.customer_id]
                              ? getCustomerDisplayName(customersById[formData.customer_id])
                              : 'Not selected'}
                          </p>
                        </div>
                        <div className="rounded-xl border border-gray-200 bg-gray-50/70 p-4 dark:border-neutral-800 dark:bg-neutral-950/40">
                          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{documentLabel} Number</div>
                          <p className="mt-2 text-sm font-semibold text-gray-900 dark:text-white">
                            {formData.number && formData.number.trim() !== ''
                              ? formData.number
                              : (nextDocumentNumber || 'Will be auto-generated on save')}
                          </p>
                        </div>
                        <div className="rounded-xl border border-gray-200 bg-gray-50/70 p-4 dark:border-neutral-800 dark:bg-neutral-950/40">
                          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Issue Date</div>
                          <p className="mt-2 text-sm font-semibold text-gray-900 dark:text-white">{formData.issue_date}</p>
                        </div>
                        <div className="rounded-xl border border-gray-200 bg-gray-50/70 p-4 dark:border-neutral-800 dark:bg-neutral-950/40">
                          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{secondaryDateLabel}</div>
                          <p className="mt-2 text-sm font-semibold text-gray-900 dark:text-white">
                            {isEstimateMode ? formData.expiry_date : formData.due_date}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="rounded-2xl border border-gray-200 bg-white p-4 elev-1 dark:bg-neutral-900 dark:border-neutral-800">
                      <div className="mb-4 flex items-start justify-between gap-3">
                        <div>
                          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Line Items</h3>
                          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                            Review quantities, rates, and amounts exactly as they will be saved.
                          </p>
                        </div>
                      </div>

                      <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-neutral-800">
                        <div className="hidden grid-cols-[minmax(0,1.6fr)_96px_120px_120px] gap-4 border-b border-gray-200 bg-gray-50 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:border-neutral-800 dark:bg-neutral-950/40 dark:text-gray-400 md:grid">
                          <span>Item</span>
                          <span className="text-right">Qty</span>
                          <span className="text-right">Rate</span>
                          <span className="text-right">Amount</span>
                        </div>

                        <div className="divide-y divide-gray-200 dark:divide-neutral-800" role="list" aria-label={`${documentLabel} items`}>
                          {formData.items.map((item, index) => (
                            <div
                              key={item.client_line_key || item.id || item.item_id || item.description || 'line-item'}
                              className="grid gap-4 px-4 py-4 md:grid-cols-[minmax(0,1.6fr)_96px_120px_120px] md:items-start"
                              role="listitem"
                            >
                              <div className="min-w-0">
                                <div className="text-sm font-semibold text-gray-900 dark:text-white">
                                  {item.name || item.description || `Line item ${index + 1}`}
                                </div>
                                {item.description && item.description !== item.name ? (
                                  <div className="mt-1 whitespace-pre-wrap text-sm text-gray-500 dark:text-gray-400">
                                    {item.description}
                                  </div>
                                ) : null}
                                <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                                  {item.is_taxable ? 'Taxable' : 'Non-taxable'}
                                </div>
                              </div>

                              <div className="flex items-center justify-between gap-4 md:block md:text-right">
                                <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 md:hidden">Qty</span>
                                <span className="text-sm font-semibold tabular-nums text-gray-900 dark:text-white">{item.qty}</span>
                              </div>

                              <div className="flex items-center justify-between gap-4 md:block md:text-right">
                                <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 md:hidden">Rate</span>
                                <span className="text-sm font-semibold tabular-nums text-gray-900 dark:text-white">{formatCurrency(item.rate)}</span>
                              </div>

                              <div className="flex items-center justify-between gap-4 md:block md:text-right">
                                <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 md:hidden">Amount</span>
                                <span className="text-sm font-extrabold tabular-nums text-gray-900 dark:text-white">{formatCurrency(item.amount)}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {(formData.terms?.trim() || formData.notes?.trim()) && (
                      <div className="rounded-2xl border border-gray-200 bg-white p-4 elev-1 dark:bg-neutral-900 dark:border-neutral-800">
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Terms & Notes</h3>
                        {formData.terms?.trim() && (
                          <div className="mt-4">
                            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Terms</div>
                            <p className="mt-2 whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300">{formData.terms}</p>
                          </div>
                        )}
                        {formData.notes?.trim() && (
                          <div className="mt-4">
                            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Notes</div>
                            <p className="mt-2 whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300">{formData.notes}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="space-y-5 lg:col-span-5">
                    <div className="rounded-2xl border border-gray-200 bg-white p-4 elev-1 dark:bg-neutral-900 dark:border-neutral-800">
                      <div className="mb-4">
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Financial Summary</h3>
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                          Double-check the breakdown before this {documentLabelLower} is saved.
                        </p>
                      </div>

                      <div className="space-y-4">
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500">Subtotal</span>
                          <span className="text-sm font-semibold tabular-nums text-gray-900 dark:text-white">{formatCurrency(totals?.subtotal || 0)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500">Tax ({businessSettings?.tax_rate || 8}%)</span>
                          <span className="text-sm font-semibold tabular-nums text-gray-900 dark:text-white">{formatCurrency(totals?.taxAmount || 0)}</span>
                        </div>
                        {!isEstimateMode && !!totals?.lateFee && totals.lateFee > 0 && (
                          <div className="flex justify-between text-red-700 dark:text-red-300">
                            <span className="text-sm">Late Fee</span>
                            <span className="text-sm font-semibold tabular-nums">{formatCurrency(totals.lateFee)}</span>
                          </div>
                        )}
                        <div className="border-t border-gray-200 pt-4 dark:border-neutral-800">
                          <div className="flex justify-between text-lg font-extrabold">
                            <span>{summaryTotalLabel}</span>
                            <span className="tabular-nums">{formatCurrency(totals?.finalTotal || 0)}</span>
                          </div>
                        </div>
                        <div className="rounded-xl border border-sedra/20 bg-sedra/5 px-3 py-3 text-sm text-sedra dark:border-sedra/30 dark:bg-sedra/10">
                          {formData.number && formData.number.trim() !== ''
                            ? `${documentLabel} number: ${formData.number}`
                            : `Next ${documentLabelLower} number: ${nextDocumentNumber || 'auto-generated on save'}`}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </main>

          <footer className="mt-8 flex flex-col gap-3 border-t border-gray-200 pt-5 dark:border-neutral-800 sm:flex-row sm:items-center sm:justify-between">
            <button
              onClick={prevStep}
              disabled={currentStep === 'edit'}
              className="inline-flex items-center justify-center rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra disabled:cursor-not-allowed disabled:opacity-60 dark:bg-neutral-900 dark:border-neutral-700 dark:text-neutral-200"
              aria-label="Go to previous step"
            >
              <FiChevronLeft className="mr-2 h-4 w-4" aria-hidden="true" />
              Previous
            </button>

            <div className="flex flex-col gap-3 sm:flex-row">
              {currentStep === 'review' ? (
                <>
                  {!isEditingDocument && (
                    <button
                      onClick={handleSaveAndCreateAnother}
                      disabled={saving}
                      className="rounded-xl border border-sedra bg-white px-4 py-2.5 text-sm font-semibold text-sedra transition-colors hover:bg-sedra/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra disabled:cursor-not-allowed disabled:opacity-60"
                      aria-label={saving ? `Saving ${documentLabelLower}...` : `Save and create another ${documentLabelLower}`}
                      aria-busy={saving}
                    >
                      {saving ? 'Saving...' : 'Save & create another'}
                    </button>
                  )}
                  <button
                    onClick={() => handleSubmit()}
                    disabled={saving}
                    className="rounded-xl bg-sedra px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-sedra-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra disabled:cursor-not-allowed disabled:opacity-60"
                    aria-label={saving ? `Saving ${documentLabelLower}...` : isEditingDocument ? `Save ${documentLabelLower} changes` : `Save ${documentLabelLower}`}
                    aria-busy={saving}
                  >
                    <FiSave className="mr-2 inline h-4 w-4" aria-hidden="true" />
                    {saving ? 'Saving...' : isEditingDocument ? `Save ${documentLabel} Changes` : `Save ${documentLabel}`}
                  </button>
                </>
              ) : (
                <button
                  onClick={nextStep}
                  disabled={!canProceedToReview}
                  className="rounded-xl bg-sedra px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-sedra-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra disabled:cursor-not-allowed disabled:opacity-60"
                  aria-label={isEditingDocument ? 'Review invoice changes' : 'Go to next step'}
                >
                  {isEditingDocument ? 'Review Changes' : 'Next'}
                  <FiChevronRight className="ml-2 inline h-4 w-4" aria-hidden="true" />
                </button>
              )}
            </div>
          </footer>
        </div>
      </div>
    </>
  );

  return isPagePresentation ? (
    <div className="mx-auto max-w-6xl px-6 py-8 lg:px-8">
      <div className="relative w-full rounded-3xl border border-gray-200 bg-white shadow-[0_10px_30px_rgba(0,0,0,.06)] dark:border-neutral-800 dark:bg-neutral-900">
        {wizardContent}
      </div>
      {showMismatchWarning && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 px-4">
          <div className="w-full max-w-lg rounded-2xl border border-amber-200 bg-white p-6 shadow-2xl dark:border-amber-800 dark:bg-neutral-900">
            <h2 className="text-lg font-bold text-amber-700 dark:text-amber-300">Warning</h2>
            <p className="mt-3 text-sm text-gray-700 dark:text-gray-200">
              WARNING: This will cause a mis-match between the estimate and the associated invoice, ensure you edit the invoice to match for records!
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowMismatchWarning(false);
                  setPendingSaveConfirmation(null);
                }}
                className="rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 dark:bg-neutral-900 dark:border-neutral-700 dark:text-neutral-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmedEstimateMismatchSave}
                className="rounded-xl bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-700"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  ) : (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="invoices-heading"
      aria-label={`${createHeading} Dialog`}
    >
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={dismissWizard}
        aria-hidden="true"
      />
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-7xl rounded-3xl border border-gray-200 bg-white shadow-[0_10px_30px_rgba(0,0,0,.08)] dark:border-neutral-800 dark:bg-neutral-900">
          {wizardContent}
        </div>
      </div>
      {showMismatchWarning && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 px-4">
          <div className="w-full max-w-lg rounded-2xl border border-amber-200 bg-white p-6 shadow-2xl dark:border-amber-800 dark:bg-neutral-900">
            <h2 className="text-lg font-bold text-amber-700 dark:text-amber-300">Warning</h2>
            <p className="mt-3 text-sm text-gray-700 dark:text-gray-200">
              WARNING: This will cause a mis-match between the estimate and the associated invoice, ensure you edit the invoice to match for records!
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowMismatchWarning(false);
                  setPendingSaveConfirmation(null);
                }}
                className="rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 dark:bg-neutral-900 dark:border-neutral-700 dark:text-neutral-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmedEstimateMismatchSave}
                className="rounded-xl bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-700"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceFormWizard; 
